import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  handleCreateStudyStudiesPostMutation,
  handleListStudiesStudiesGetQueryKey,
} from '@/api/generated/@tanstack/react-query.gen';
import {
  GCI_SWEEP_AVAILABLE,
  SURROGATE_MODEL_AVAILABLE,
} from './-data/feature-flags';
import { Spinner } from '@/components/ui/spinner';
import { RowLabel } from '@/components/RowLabel';

type Kind = 'doe' | 'surrogate' | 'gci';

interface MethodOption {
  kind: Kind;
  numeral: string;
  title: ParseKeys;
  subtitle: ParseKeys;
  description: ParseKeys;
  bullets: ParseKeys[];
  diagram: ReactNode;
  diagramCaption: ParseKeys;
  disabled?: boolean;
  badge?: ParseKeys;
}

export const METHODS: MethodOption[] = [
  {
    kind: 'doe',
    numeral: '01',
    title: 'simopsNew.methods.doe.title',
    subtitle: 'simopsNew.methods.doe.subtitle',
    description: 'simopsNew.methods.doe.description',
    bullets: [
      'simopsNew.methods.doe.bullet1',
      'simopsNew.methods.doe.bullet2',
      'simopsNew.methods.doe.bullet3',
    ],
    diagramCaption: 'simopsNew.methods.doe.diagramCaption',
    diagram: <DoeDiagram />,
  },
  {
    kind: 'surrogate',
    numeral: '02',
    title: 'simopsNew.methods.surrogate.title',
    subtitle: 'simopsNew.methods.surrogate.subtitle',
    description: 'simopsNew.methods.surrogate.description',
    bullets: [
      'simopsNew.methods.surrogate.bullet1',
      'simopsNew.methods.surrogate.bullet2',
      'simopsNew.methods.surrogate.bullet3',
    ],
    diagramCaption: 'simopsNew.methods.surrogate.diagramCaption',
    diagram: <SurrogateDiagram />,
    disabled: !SURROGATE_MODEL_AVAILABLE,
    badge: SURROGATE_MODEL_AVAILABLE ? undefined : 'simopsNew.comingSoon',
  },
  {
    kind: 'gci',
    numeral: '03',
    title: 'simopsNew.methods.gci.title',
    subtitle: 'simopsNew.methods.gci.subtitle',
    description: 'simopsNew.methods.gci.description',
    bullets: [
      'simopsNew.methods.gci.bullet1',
      'simopsNew.methods.gci.bullet2',
      'simopsNew.methods.gci.bullet3',
    ],
    diagramCaption: 'simopsNew.methods.gci.diagramCaption',
    diagram: <GciDiagram />,
    disabled: !GCI_SWEEP_AVAILABLE,
    badge: GCI_SWEEP_AVAILABLE ? undefined : 'simopsNew.comingSoon',
  },
];

function NewSimOpsStudyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mesh } = useSearch({ from: '/_app/simops/new' });
  const [selected, setSelected] = useState<Kind | null>(null);

  const createStudy = useMutation({
    ...handleCreateStudyStudiesPostMutation(),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: handleListStudiesStudiesGetQueryKey(),
      });
      void navigate({
        to: '/simops/$studyId',
        params: { studyId: data.id },
      });
    },
  });

  const handleContinue = () => {
    if (!selected) return;
    createStudy.mutate({
      body: {
        // Name is omitted so the backend auto-generates it from the study's
        // source geometry/mesh (ENG-1001); the user renames it in the editor.
        config: {
          kind: selected,
          // Seed the mesh as the study input when launched from MeshGen
          // (ENG-757). initDraftConfig rehydrates selected_inputs from
          // config.inputs.mesh_ids on the study page.
          ...(mesh ? { inputs: { mesh_ids: [mesh] } } : {}),
        },
      },
    });
  };

  const handleBack = () => {
    void navigate({ to: '/simops' });
  };

  return (
    <div className="h-full flex-1 overflow-auto">
      <div className="mx-auto max-w-7xl px-8 py-8 md:px-12 md:py-10">
        {/* breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <button
            onClick={handleBack}
            className="transition-colors hover:text-foreground"
            type="button"
          >
            {t('simopsNew.breadcrumb.studies')}
          </button>
          <span>/</span>
          <span className="text-foreground">
            {t('simopsNew.breadcrumb.newStudy')}
          </span>
        </div>

        {/* hero */}
        <div className="mt-10 max-w-2xl">
          <RowLabel size="2xs" className="flex gap-2.5 font-mono">
            <span className="font-semibold text-foreground">
              {t('simopsNew.hero.step')}
            </span>
            <span>·</span>
            <span>{t('simopsNew.hero.methodology')}</span>
          </RowLabel>
          <h1 className="mt-5 text-3xl leading-tight font-semibold tracking-tight text-foreground md:text-4xl">
            {t('simopsNew.hero.title')}
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            {t('simopsNew.hero.description')}
          </p>
        </div>

        {/* cards */}
        <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {METHODS.map((m) => (
            <MethodCard
              key={m.kind}
              option={m}
              selected={selected === m.kind}
              onSelect={() => !m.disabled && setSelected(m.kind)}
            />
          ))}
        </div>

        {/* footer */}
        <div className="mt-9 flex flex-col gap-4 border-t border-border pt-5 md:flex-row md:items-center md:justify-between">
          <p className="max-w-lg text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">
              {t('simopsNew.footer.selectingMethod')}
            </span>{' '}
            {t('simopsNew.footer.rest')}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              type="button"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('simopsNew.footer.back')}
            </button>
            <button
              onClick={handleContinue}
              disabled={!selected || createStudy.isPending}
              className={cn(
                'inline-flex items-center gap-2 rounded-md border border-foreground bg-foreground px-4 py-2 text-sm font-medium text-background transition-opacity',
                'hover:opacity-90',
                'disabled:cursor-not-allowed disabled:opacity-40',
              )}
              type="button"
            >
              {createStudy.isPending ? (
                <>
                  <Spinner aria-hidden className="size-3.5" />
                  {t('simopsNew.footer.creating')}
                </>
              ) : (
                <>
                  {t('simopsNew.footer.continue')}
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MethodCardProps {
  option: MethodOption;
  selected: boolean;
  onSelect: () => void;
}

function MethodCard({ option, selected, onSelect }: MethodCardProps) {
  const { t } = useTranslation();
  const { disabled, badge } = option;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'relative flex flex-col gap-3.5 rounded-md border border-border bg-card p-4 text-left transition-all',
        'min-h-[420px]',
        !disabled &&
          'cursor-pointer hover:border-muted-foreground/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        selected && 'border-foreground shadow-[0_0_0_1px_var(--foreground)]',
        disabled && 'cursor-not-allowed bg-muted/30',
      )}
    >
      <div className="flex items-start justify-between gap-2.5">
        <RowLabel
          className={cn(
            'pt-0.5 font-mono font-semibold tracking-caps-wide',
            // RowLabel is muted by default, which is what both the unselected
            // and the disabled numeral already were; only a live selection
            // lifts it to `text-foreground`.
            selected && !disabled && 'text-foreground',
          )}
        >
          {option.numeral}
        </RowLabel>
        {badge ? (
          <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 font-mono text-4xs font-semibold tracking-caps text-muted-foreground uppercase">
            {t(badge)}
          </span>
        ) : (
          <span
            className={cn(
              'h-3.5 w-3.5 flex-shrink-0 rounded-full border-[1.5px] transition-colors',
              selected
                ? 'border-foreground bg-foreground shadow-[inset_0_0_0_2px_var(--background)]'
                : 'border-border bg-card',
            )}
          />
        )}
      </div>

      <h2
        className={cn(
          'text-sm leading-tight font-semibold tracking-normal text-foreground',
          disabled && 'text-muted-foreground',
        )}
      >
        {t(option.title)}
      </h2>
      <RowLabel className="-mt-2">{t(option.subtitle)}</RowLabel>

      <div
        className={cn(
          'relative rounded-sm border border-border bg-muted/50 p-3 text-muted-foreground',
          disabled && 'opacity-60',
        )}
      >
        <div className="h-[110px] w-full">{option.diagram}</div>
        <div className="mt-1 text-right font-mono text-4xs tracking-caps text-muted-foreground">
          {t(option.diagramCaption)}
        </div>
      </div>

      <p
        className={cn(
          'text-xs leading-relaxed text-muted-foreground',
          disabled && 'opacity-80',
        )}
      >
        {t(option.description)}
      </p>

      <ul
        className={cn(
          'mt-auto flex flex-col gap-1 border-t border-border pt-2.5 text-2xs leading-relaxed text-foreground',
          disabled && 'opacity-70',
        )}
      >
        {option.bullets.map((b) => (
          <li key={b} className="flex items-baseline gap-2">
            <span className="h-1 w-1 flex-shrink-0 translate-y-[-1px] rounded-full bg-foreground" />
            {t(b)}
          </li>
        ))}
      </ul>
    </button>
  );
}

function DoeDiagram() {
  const cols = 6;
  const rows = 5;
  const x0 = 36;
  const y0 = 18;
  const w = 150;
  const h = 64;
  const dots: { cx: number; cy: number }[] = [];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      dots.push({
        cx: x0 + (i * w) / (cols - 1),
        cy: y0 + (j * h) / (rows - 1),
      });
    }
  }
  return (
    <svg
      viewBox="0 0 220 110"
      fill="none"
      className="block h-full w-full"
      aria-hidden
    >
      <line
        x1="20"
        y1="92"
        x2="200"
        y2="92"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="0.6"
      />
      <line
        x1="20"
        y1="12"
        x2="20"
        y2="92"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="0.6"
      />
      {dots.map((d, i) => (
        <circle
          key={i}
          cx={d.cx}
          cy={d.cy}
          r="2"
          fill="currentColor"
          opacity="0.8"
        />
      ))}
    </svg>
  );
}

function SurrogateDiagram() {
  const pts: [number, number][] = [
    [0.05, 0.5],
    [0.55, 0.12],
    [0.3, 0.78],
    [0.78, 0.4],
    [0.18, 0.25],
    [0.65, 0.68],
    [0.42, 0.55],
    [0.92, 0.22],
    [0.12, 0.92],
    [0.5, 0.32],
    [0.84, 0.85],
    [0.36, 0.08],
    [0.72, 0.95],
    [0.06, 0.7],
    [0.6, 0.92],
    [0.95, 0.6],
  ];
  const x0 = 28;
  const y0 = 18;
  const w = 165;
  const h = 68;
  return (
    <svg
      viewBox="0 0 220 110"
      fill="none"
      className="block h-full w-full"
      aria-hidden
    >
      <defs>
        <radialGradient id="gp-field" cx="40%" cy="55%" r="70%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="60%" stopColor="currentColor" stopOpacity="0.05" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="20" y="12" width="180" height="80" fill="url(#gp-field)" />
      <path
        d="M 28 70 Q 60 24 100 32 T 195 55"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="0.6"
        strokeDasharray="2 3"
        fill="none"
      />
      <path
        d="M 23 82 Q 65 50 105 60 T 200 78"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="0.6"
        strokeDasharray="2 3"
        fill="none"
      />
      <line
        x1="20"
        y1="92"
        x2="200"
        y2="92"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="0.6"
      />
      <line
        x1="20"
        y1="12"
        x2="20"
        y2="92"
        stroke="currentColor"
        strokeOpacity="0.4"
        strokeWidth="0.6"
      />
      {pts.map(([u, v], i) => (
        <circle
          key={i}
          cx={x0 + u * w}
          cy={y0 + v * h}
          r="1.9"
          fill="currentColor"
          opacity="0.8"
        />
      ))}
    </svg>
  );
}

function GciDiagram() {
  const fineLines: ReactNode[] = [];
  for (let i = 1; i < 8; i++) {
    fineLines.push(
      <line
        key={`v${i}`}
        x1={154 + (i * 44) / 8}
        y1={14}
        x2={154 + (i * 44) / 8}
        y2={54}
      />,
    );
  }
  for (let j = 1; j < 6; j++) {
    fineLines.push(
      <line
        key={`h${j}`}
        x1={154}
        y1={14 + (j * 40) / 6}
        x2={198}
        y2={14 + (j * 40) / 6}
      />,
    );
  }
  return (
    <svg
      viewBox="0 0 220 110"
      fill="none"
      className="block h-full w-full"
      aria-hidden
    >
      <g
        stroke="currentColor"
        strokeOpacity="0.5"
        strokeWidth="0.7"
        fill="none"
      >
        <rect x="22" y="14" width="44" height="40" />
        <line x1="22" y1="34" x2="66" y2="34" />
        <line x1="44" y1="14" x2="44" y2="54" />
        <text
          x="22"
          y="68"
          fontFamily="ui-monospace, monospace"
          fontSize="7.5"
          fill="currentColor"
          fillOpacity="0.6"
          stroke="none"
        >
          h₁ coarse
        </text>
      </g>
      <g
        stroke="currentColor"
        strokeOpacity="0.5"
        strokeWidth="0.6"
        fill="none"
      >
        <rect x="88" y="14" width="44" height="40" />
        <line x1="88" y1="27.3" x2="132" y2="27.3" />
        <line x1="88" y1="40.7" x2="132" y2="40.7" />
        <line x1="102.7" y1="14" x2="102.7" y2="54" />
        <line x1="117.3" y1="14" x2="117.3" y2="54" />
        <text
          x="88"
          y="68"
          fontFamily="ui-monospace, monospace"
          fontSize="7.5"
          fill="currentColor"
          fillOpacity="0.6"
          stroke="none"
        >
          h₂ medium
        </text>
      </g>
      <g
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="0.5"
        fill="none"
      >
        <rect x="154" y="14" width="44" height="40" />
        {fineLines}
        <text
          x="154"
          y="68"
          fontFamily="ui-monospace, monospace"
          fontSize="7.5"
          fill="currentColor"
          fillOpacity="0.6"
          stroke="none"
        >
          h₃ fine
        </text>
      </g>
      <path
        d="M 22 86 L 198 86"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="0.6"
        markerEnd="url(#gci-arrow)"
      />
      <defs>
        <marker
          id="gci-arrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path
            d="M 0 0 L 10 5 L 0 10 z"
            fill="currentColor"
            fillOpacity="0.55"
          />
        </marker>
      </defs>
      <text
        x="22"
        y="100"
        fontFamily="ui-monospace, monospace"
        fontSize="7.5"
        fill="currentColor"
        fillOpacity="0.55"
      >
        refinement ratio r = 2
      </text>
      <text
        x="172"
        y="100"
        fontFamily="ui-monospace, monospace"
        fontSize="7.5"
        fill="currentColor"
        fillOpacity="0.55"
      >
        → GCI
      </text>
    </svg>
  );
}

export const Route = createFileRoute('/_app/simops/new')({
  component: NewSimOpsStudyPage,
  validateSearch: (search: Record<string, unknown>): { mesh?: string } => ({
    mesh: typeof search.mesh === 'string' ? search.mesh : undefined,
  }),
});
